import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Divider,
  Tab,
  Tabs,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import ExtensionIcon from '@mui/icons-material/Extension';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import { AccountService, IAccount } from '../service/accountService';
import { PiService } from '../service/piService';
import { RaspberryPi } from '../model/RaspberryPi';
import { Link, Link as RouterLink } from 'react-router-dom';

interface AccountDetailProps {
  account: IAccount;
  onBackClick: () => void;
  onUpdate: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AccountDetail: React.FC<AccountDetailProps> = ({ account, onBackClick, onUpdate }) => {
  const [editedAccount, setEditedAccount] = useState<IAccount>({...account});
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isLoading, setIsLoading] = useState(false);

  // State for Pi assignment
  const [isAssignPiDialogOpen, setIsAssignPiDialogOpen] = useState(false);
  const [availablePis, setAvailablePis] = useState<RaspberryPi[]>([]);
  const [selectedPiId, setSelectedPiId] = useState<string>('');
  const [isAssigningPi, setIsAssigningPi] = useState(false);
  const [showUnassignDialog, setShowUnassignDialog] = useState(false);
  const [isUnassigningPi, setIsUnassigningPi] = useState(false);
  const [loadingPi, setLoadingPi] = useState(false);
  const [piDetails, setPiDetails] = useState<RaspberryPi | null>(null);
  const [hasFetchedPi, setHasFetchedPi] = useState(false); // Control flag to prevent infinite loops

  // Fetch Pi details if assigned - only runs once on initial load
  useEffect(() => {
    // Skip if we've already fetched the Pi data
    if (hasFetchedPi) {
      return;
    }

    const fetchPiDetails = async () => {
      console.log('[AccountDetail] Running initial Pi details fetch for account:', account._id);
      setLoadingPi(true);
      setHasFetchedPi(true); // Mark as fetched to prevent infinite loops
      
      try {
        // First try to fetch by owner ID directly - this is the most reliable method
        console.log('[AccountDetail] Attempting to fetch Pi by owner ID first:', account._id);
        const ownerPis = await PiService.getPiForOwner(account._id);
        
        if (ownerPis && ownerPis.length > 0) {
          console.log('[AccountDetail] Found Pi by owner lookup:', ownerPis[0].name);
          setPiDetails(ownerPis[0]);
          setLoadingPi(false);
          return;
        } else {
          console.log('[AccountDetail] No Pis found by owner lookup');
          
          // If we don't find any Pis by owner ID and there's an explicit assignment ref, try that
          if (account.assignedRaspberryPiRef) {
            console.log('[AccountDetail] Trying to fetch by assignedRaspberryPiRef:', account.assignedRaspberryPiRef);
            
            // Try to fetch by ID if it's a valid MongoDB ObjectId
            const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(account.assignedRaspberryPiRef);
            let pi;
            
            if (isValidObjectId) {
              console.log('[AccountDetail] Fetching Pi by ID:', account.assignedRaspberryPiRef);
              pi = await PiService.getPiById(account.assignedRaspberryPiRef);
            } else {
              // Try to fetch by serial number if not an ObjectId
              console.log('[AccountDetail] Fetching Pi by serial number:', account.assignedRaspberryPiRef);
              pi = await PiService.getPiBySerialNumber(account.assignedRaspberryPiRef);
            }
            
            console.log('[AccountDetail] Pi details fetched:', pi ? pi.name : 'null');
            setPiDetails(pi);
          } else if (account.raspberryPi) {
            // If the account already has the Pi object, use it directly
            console.log('[AccountDetail] Using Pi details from account object');
            setPiDetails(account.raspberryPi);
          } else {
            // No Pi assigned
            console.log('[AccountDetail] No Pi assignment found');
            setPiDetails(null);
          }
        }
      } catch (error) {
        console.error("[AccountDetail] Failed to fetch Pi details:", error);
        setPiDetails(null);
      } finally {
        setLoadingPi(false);
      }
    };

    fetchPiDetails();
  }, [account._id, account.assignedRaspberryPiRef, account.raspberryPi, hasFetchedPi]);

  // If account data changes (like after a refresh), allow a re-fetch
  useEffect(() => {
    if (account && hasFetchedPi) {
      setHasFetchedPi(false);
    }
  }, [account._id]); // Only re-fetch if the account ID changes

  // Load available Pis for assignment
  const fetchAvailablePis = async () => {
    try {
      setIsLoading(true);
      const pis = await PiService.getAvailableRaspberryPis();
      setAvailablePis(pis);
    } catch (error) {
      console.error("Fehler beim Laden verfügbarer Raspberry Pis:", error);
      showSnackbar("Fehler beim Laden verfügbarer Pis", "error");
      setAvailablePis([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setEditedAccount(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleAdmin = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedAccount(prev => ({
      ...prev,
      isAdmin: event.target.checked
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Implementiere hier den API-Aufruf zum Speichern der Änderungen
      // await AccountService.updateAccount(editedAccount._id, editedAccount);
      
      // Mock für jetzt
      console.log('Account würde gespeichert werden:', editedAccount);
      
      showSnackbar('Account erfolgreich aktualisiert', 'success');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Fehler beim Speichern des Accounts:', error);
      showSnackbar('Fehler beim Speichern des Accounts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      // Implementiere hier den API-Aufruf zum Löschen des Accounts
      // await AccountService.deleteAccount(account._id);
      
      // Mock für jetzt
      console.log('Account würde gelöscht werden:', account._id);
      
      showSnackbar('Account erfolgreich gelöscht', 'success');
      setTimeout(() => {
        onBackClick();
      }, 2000);
    } catch (error) {
      console.error('Fehler beim Löschen des Accounts:', error);
      showSnackbar('Fehler beim Löschen des Accounts', 'error');
    } finally {
      setShowDeleteDialog(false);
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      showSnackbar('Die Passwörter stimmen nicht überein', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await AccountService.changePassword(account._id, newPassword);
      showSnackbar('Passwort erfolgreich zurückgesetzt', 'success');
      setShowResetPasswordDialog(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Passworts:', error);
      showSnackbar('Fehler beim Zurücksetzen des Passworts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAssignPiDialog = () => {
    fetchAvailablePis();
    setSelectedPiId('');
    setIsAssignPiDialogOpen(true);
  };

  const handleCloseAssignPiDialog = () => {
    setIsAssignPiDialogOpen(false);
  };

  const handleAssignPi = async () => {
    if (!selectedPiId) {
      showSnackbar("Bitte wählen Sie einen Raspberry Pi aus.", "error");
      return;
    }
    
    console.log(`[AccountDetail] Starting Pi assignment process: Pi=${selectedPiId}, Account=${account._id}`);
    setIsAssigningPi(true);
    
    try {
      // First, get current Pi details to check its status
      try {
        const currentPi = await PiService.getPiById(selectedPiId);
        console.log(`[AccountDetail] Current Pi status before assignment:`, {
          id: currentPi._id,
          name: currentPi.name,
          currentOwnerId: currentPi.ownerId || 'none',
          status: currentPi.status
        });
        
        if (currentPi.ownerId) {
          console.warn(`[AccountDetail] Warning: Pi is already assigned to owner: ${currentPi.ownerId}`);
        }
      } catch (piCheckError) {
        console.error('[AccountDetail] Error checking Pi status before assignment:', piCheckError);
        // Continue with assignment anyway
      }
      
      // Perform the assignment
      console.log(`[AccountDetail] Calling AccountService.assignRaspberryPi(${account._id}, ${selectedPiId})`);
      await AccountService.assignRaspberryPi(account._id, selectedPiId);
      
      // Wait a moment to ensure backend processing is complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // After successful assignment, load Pi details
      try {
        console.log(`[AccountDetail] Assignment successful, loading Pi details`);
        const pi = await PiService.getPiById(selectedPiId);
        console.log(`[AccountDetail] Loaded Pi details:`, {
          id: pi._id,
          name: pi.name,
          ownerId: pi.ownerId,
          expectedOwnerId: account._id
        });
        
        // Verify the owner assignment was successful
        // The API might not have returned an ownerId, so let's verify by checking owner Pis directly
        let piAssignmentVerified = false;
        
        try {
          console.log(`[AccountDetail] Verifying assignment via owner lookup`);
          const ownerPis = await PiService.getPiForOwner(account._id);
          
          // Check if our Pi is in the list of Pis assigned to this owner
          const assignedPi = ownerPis.find(p => p._id === selectedPiId);
          
          if (assignedPi) {
            console.log(`[AccountDetail] Assignment verified: Pi found in owner's Pis list`);
            piAssignmentVerified = true;
            
            // Use this Pi from the verified list
            pi.ownerId = account._id; // Make sure ownerId is set
            
            // Update local state with the Pi that was verified to be assigned
            setEditedAccount(prev => ({
              ...prev,
              raspberryPi: assignedPi,
              assignedRaspberryPiRef: selectedPiId
            }));
            
            // Set Pi details for UI display
            setPiDetails(assignedPi);
          } else {
            console.warn(`[AccountDetail] Assignment verification failed: Pi not found in owner's Pis list`);
          }
        } catch (verifyError) {
          console.error(`[AccountDetail] Error verifying assignment:`, verifyError);
        }
        
        // If we couldn't verify through owner lookup, but we know the assignment API call succeeded,
        // we'll still update the UI
        if (!piAssignmentVerified) {
          console.log(`[AccountDetail] Using direct Pi data for UI update`);
          
          // Assume assignment was successful even if verification failed
          setEditedAccount(prev => ({
            ...prev,
            raspberryPi: pi,
            assignedRaspberryPiRef: selectedPiId
          }));
          
          // Set Pi details for UI display
          setPiDetails(pi);
        }
        
        showSnackbar("Raspberry Pi erfolgreich zugewiesen.", "success");
        
      } catch (detailsError) {
        console.error("[AccountDetail] Error loading Pi details after assignment:", detailsError);
        
        // Even if details couldn't be loaded, try to verify assignment directly
        try {
          console.log(`[AccountDetail] Attempting to verify assignment through owner lookup`);
          const ownerPis = await PiService.getPiForOwner(account._id);
          const assignedPi = ownerPis.find(p => p._id === selectedPiId);
          
          if (assignedPi) {
            console.log(`[AccountDetail] Assignment verified through owner lookup`);
            setEditedAccount(prev => ({
              ...prev,
              raspberryPi: assignedPi,
              assignedRaspberryPiRef: selectedPiId
            }));
            setPiDetails(assignedPi);
          } else {
            // Fallback to basic reference update
            setEditedAccount(prev => ({
              ...prev,
              assignedRaspberryPiRef: selectedPiId
            }));
          }
        } catch (verifyError) {
          console.error(`[AccountDetail] Error in fallback verification:`, verifyError);
          // Still update the reference so UI shows something
          setEditedAccount(prev => ({
            ...prev,
            assignedRaspberryPiRef: selectedPiId
          }));
        }
        
        showSnackbar("Raspberry Pi zugewiesen, aber Details konnten nicht geladen werden.", "success");
      }
      
      setIsAssignPiDialogOpen(false);
      
      // Delayed update to prevent immediate navigation back to list view
      setTimeout(() => {
        onUpdate();
      }, 1000);
      
    } catch (error: any) {
      console.error("[AccountDetail] Error assigning Pi:", error);
      
      // More helpful error message based on the error
      let errorMessage = "Fehler beim Zuweisen des Raspberry Pi.";
      
      if (error.message && error.message.includes("owner ID mismatch")) {
        errorMessage = "Die Zuweisung wurde vom Server akzeptiert, aber die Bestätigung ist fehlgeschlagen. Bitte überprüfen Sie nach ein paar Sekunden, ob der Pi zugewiesen wurde.";
      } else if (error.response && error.response.data && error.response.data.message) {
        errorMessage = `Server-Fehler: ${error.response.data.message}`;
      }
      
      showSnackbar(errorMessage, "error");
    } finally {
      setIsAssigningPi(false);
    }
  };

  const handleOpenUnassignDialog = () => {
    setShowUnassignDialog(true);
  };

  const handleCloseUnassignDialog = () => {
    setShowUnassignDialog(false);
  };

  const handleConfirmUnassignPi = async () => {
    setIsUnassigningPi(true);
    try {
      await AccountService.unassignRaspberryPi(account._id);
      
      // Lokalen Account-Zustand aktualisieren ohne vollständigen Refresh
      setEditedAccount(prev => ({
        ...prev,
        raspberryPi: undefined,
        assignedRaspberryPiRef: undefined
      }));
      
      // Pi-Details zurücksetzen
      setPiDetails(null);
      
      showSnackbar("Raspberry Pi Zuweisung erfolgreich aufgehoben.", "success");
      setShowUnassignDialog(false);
      
      // Verzögerte Aktualisierung, damit wir nicht sofort zur Listenansicht wechseln
      setTimeout(() => {
        onUpdate();
      }, 1000);
    } catch (error) {
      console.error("Fehler beim Aufheben der Pi-Zuweisung:", error);
      showSnackbar("Fehler beim Aufheben der Zuweisung.", "error");
    } finally {
      setIsUnassigningPi(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (dateString?: Date): string => {
    if (!dateString) return 'Kein Datum';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Helper function to determine if a Pi is assigned
  const isPiAssigned = (): boolean => {
    // Check if we have Pi details loaded from the direct owner lookup
    // This is the most reliable indicator
    if (piDetails) {
      console.log('[AccountDetail] isPiAssigned: Pi details present from owner lookup');
      return true;
    }
    
    // Check for explicit assignment fields as fallback
    if (account.assignedRaspberryPiRef || account.raspberryPi) {
      console.log('[AccountDetail] isPiAssigned: Direct assignment fields present');
      return true;
    }
    
    // Do NOT use subdomain or other fields - only direct owner relationships matter
    console.log('[AccountDetail] isPiAssigned: No Pi assignment indicators found');
    return false;
  };

  // Helper function to get the Pi name
  const getPiName = (): string => {
    console.log('[AccountDetail] getPiName sources:', {
      fromPiDetails: piDetails?.name,
      fromAccountPi: account.raspberryPi?.name,
      fromRefString: account.assignedRaspberryPiRef
    });
    
    // First check piDetails, then raspberryPi on account, then fallbacks
    if (piDetails) return `${piDetails.name} (${piDetails.serialNumber})`;
    if (account.raspberryPi) return `${account.raspberryPi.name} (${account.raspberryPi.serialNumber})`;
    
    // Only use the reference as fallback if present
    if (account.assignedRaspberryPiRef) return account.assignedRaspberryPiRef;
    
    // Should never get here if isPiAssigned() is used correctly
    return 'Unknown Pi';
  };

  return (
    <Box>
      {/* Header mit Zurück-Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBackClick} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h2">
          {account.displayName} Details
        </Typography>
      </Box>

      {/* Account-Status Banner */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ mr: 2 }}>{account.displayName}</Typography>
        {account.isAdmin ? (
          <Chip 
            icon={<AdminPanelSettingsIcon />} 
            label="Administrator"
            color="primary"
          />
        ) : (
          <Chip 
            icon={<PersonIcon />} 
            label="Kunde"
            variant="outlined"
          />
        )}
      </Box>

      {/* Tabs für verschiedene Abschnitte */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="account tabs">
          <Tab icon={<PersonIcon />} label="Allgemein" />
          <Tab icon={<CameraAltIcon />} label="Kameras" />
          <Tab icon={<SettingsIcon />} label="Einstellungen" />
          <Tab icon={<ExtensionIcon />} label="Erweiterungen" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              {!isEditing ? (
                <Button 
                  startIcon={<EditIcon />} 
                  variant="contained" 
                  onClick={() => setIsEditing(true)}
                >
                  Bearbeiten
                </Button>
              ) : (
                <Button 
                  startIcon={<SaveIcon />} 
                  variant="contained" 
                  color="success"
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  Speichern
                </Button>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="displayName"
                  value={editedAccount.displayName}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={editedAccount.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Abonnement gültig bis"
                  value={formatDate(editedAccount.subscribedUntil)}
                  variant="outlined"
                  margin="normal"
                  disabled={true}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedAccount.isAdmin}
                      onChange={handleToggleAdmin}
                      disabled={!isEditing}
                    />
                  }
                  label="Administrator-Rechte"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Aktionen</Typography>
              <Button
                variant="outlined"
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => setShowResetPasswordDialog(true)}
              >
                Passwort zurücksetzen
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setShowDeleteDialog(true)}
              >
                Account löschen
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Kameras</Typography>
            {account.cameras && account.cameras.length > 0 ? (
              <Grid container spacing={2}>
                {account.cameras.map((camera, index) => (
                  <Grid item xs={12} sm={6} md={4} key={camera.ref}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1">{camera.displayName}</Typography>
                      <Typography variant="body2" color="text.secondary">URL: {camera.url}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Abstandsschwelle: {camera.abstandsschwelle.toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body1">Keine Kameras vorhanden.</Typography>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>PLC-Einstellungen</Typography>
            <TextField
              fullWidth
              label="PLC URL"
              name="plcUrl"
              value={editedAccount.plcUrl || ''}
              onChange={handleInputChange}
              variant="outlined"
              margin="normal"
              disabled={!isEditing}
              helperText="URL für die PLC-Verbindung"
            />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DeveloperBoardIcon sx={{ mr: 1 }} /> Raspberry Pi
            </Typography>
            
            {loadingPi ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
                <Typography sx={{ ml: 2 }}>Lade Raspberry Pi Informationen...</Typography>
              </Box>
            ) : isPiAssigned() ? (
              <Box sx={{ mb: 3 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {getPiName()}
                  </Typography>
                  
                  {piDetails ? (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Seriennummer:</strong> {piDetails.serialNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Status:</strong> {piDetails.status}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>IP-Adresse:</strong> {piDetails.ipAddress || 'Nicht verfügbar'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Subdomain:</strong> {piDetails.subdomain || 'Nicht verfügbar'}
                        </Typography>
                      </Grid>
                      {piDetails.lastSeen && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Zuletzt gesehen:</strong> {new Date(piDetails.lastSeen).toLocaleString('de-DE')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  ) : account.raspberryPi ? (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {/* Display fields from account.raspberryPi if available */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Seriennummer:</strong> {account.raspberryPi.serialNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Status:</strong> {account.raspberryPi.status}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Raspberry Pi Zuweisung erkannt, aber detaillierte Informationen konnten nicht geladen werden.
                      Das System hat festgestellt, dass dieser Account einen Pi besitzt, konnte aber keine Details dazu abrufen.
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleOpenUnassignDialog}
                      disabled={isUnassigningPi}
                      startIcon={isUnassigningPi ? <CircularProgress size={20} /> : <DeleteIcon />}
                      sx={{ minWidth: '200px' }}
                    >
                      {isUnassigningPi ? 'Wird entfernt...' : 'Pi-Zuweisung entfernen'}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ mr: 2 }}>Kein Raspberry Pi zugewiesen.</Typography>
                <Button
                  variant="contained"
                  onClick={handleOpenAssignPiDialog}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={20} /> : 'Zuweisen'}
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Lösch-Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Account löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie den Account "{account.displayName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button onClick={handleDelete} color="error" disabled={isLoading}>
            {isLoading ? 'Wird gelöscht...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Passwort-Reset-Dialog */}
      <Dialog
        open={showResetPasswordDialog}
        onClose={() => setShowResetPasswordDialog(false)}
      >
        <DialogTitle>Passwort zurücksetzen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Neues Passwort für "{account.displayName}" eingeben:
          </DialogContentText>
          <TextField
            fullWidth
            label="Neues Passwort"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="dense"
          />
          <TextField
            fullWidth
            label="Passwort bestätigen"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowResetPasswordDialog(false);
              setNewPassword('');
              setConfirmPassword('');
            }} 
            disabled={isLoading}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleResetPassword} 
            color="primary" 
            disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          >
            {isLoading ? 'Wird geändert...' : 'Passwort ändern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pi Assignment Dialog */}
      <Dialog 
        open={isAssignPiDialogOpen} 
        onClose={handleCloseAssignPiDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Raspberry Pi zuweisen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Wählen Sie einen verfügbaren Raspberry Pi, um ihn diesem Account zuzuweisen.
          </DialogContentText>
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : availablePis.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Keine verfügbaren Raspberry Pis gefunden. Alle Pis sind bereits zugewiesen.
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="pi-select-label">Raspberry Pi</InputLabel>
              <Select
                labelId="pi-select-label"
                value={selectedPiId}
                label="Raspberry Pi"
                onChange={(e) => setSelectedPiId(e.target.value as string)}
              >
                {availablePis.map(pi => (
                  <MenuItem key={pi._id} value={pi._id}>
                    {pi.name} ({pi.serialNumber})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignPiDialog} disabled={isAssigningPi}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleAssignPi}
            color="primary"
            disabled={isAssigningPi || !selectedPiId}
          >
            {isAssigningPi ? <CircularProgress size={24} /> : 'Zuweisen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pi Unassignment Dialog */}
      <Dialog
        open={showUnassignDialog}
        onClose={handleCloseUnassignDialog}
      >
        <DialogTitle>Raspberry Pi Zuweisung aufheben</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie die Zuweisung des Raspberry Pi wirklich aufheben? 
            Der Raspberry Pi wird dadurch wieder verfügbar für andere Accounts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnassignDialog} disabled={isUnassigningPi}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmUnassignPi}
            color="error"
            disabled={isUnassigningPi}
          >
            {isUnassigningPi ? <CircularProgress size={24} /> : 'Zuweisung aufheben'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountDetail; 